import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

type DrawerTrackerContextType = {
  registerDrawer: (open: boolean) => void;
  isAnyDrawerOpen: boolean;
};

const DrawerTrackerContext = createContext<
  DrawerTrackerContextType | undefined
>(undefined);

type DrawerTrackerProviderProps = {
  children: ReactNode;
};

export const DrawerTrackerProvider = ({
  children,
}: DrawerTrackerProviderProps) => {
  const [openCount, setOpenCount] = useState(0);

  const registerDrawer = useCallback((open: boolean) => {
    setOpenCount(count => Math.max(0, count + (open ? 1 : -1)));
  }, []);

  const contextValue = useMemo(
    () => ({ registerDrawer, isAnyDrawerOpen: openCount > 0 }),
    [registerDrawer, openCount],
  );

  return (
    <DrawerTrackerContext.Provider value={contextValue}>
      {children}
    </DrawerTrackerContext.Provider>
  );
};

DrawerTrackerContext.displayName = 'DrawerTrackerContext';

// Registers `isOpen` with the nearest DrawerTrackerProvider while true, and
// deregisters on close/unmount. Safe to call without a provider mounted
// (e.g. in isolated component tests) - it simply becomes a no-op.
export const useDrawerOpenTracker = (isOpen: boolean) => {
  const context = useContext(DrawerTrackerContext);

  useEffect(() => {
    if (!isOpen || !context) return;

    context.registerDrawer(true);
    return () => context.registerDrawer(false);
  }, [isOpen, context]);
};

// True while at least one drawer registered via useDrawerOpenTracker is
// open. Returns false when used outside a provider.
export const useIsAnyDrawerOpen = (): boolean => {
  const context = useContext(DrawerTrackerContext);

  return context?.isAnyDrawerOpen ?? false;
};

export default DrawerTrackerContext;
